import React, {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  Bug,
  CheckCircle,
  Clock,
  WarningCircle,
  ImageSquare,
  X,
} from "@phosphor-icons/react";

import { toast } from "sonner";

import {
  platformSupport,
} from "@/api";


const STATUS_LABELS = {
  new: "Nouveau",
  triage: "À qualifier",
  in_progress: "En cours",
  waiting_reporter:
    "En attente demandeur",
  planned: "Planifié",
  resolved: "Résolu",
  closed: "Fermé",
  rejected: "Refusé",
  duplicate: "Doublon",
};


const PRIORITY_LABELS = {
  p1: "P1 Critique",
  p2: "P2 Haute",
  p3: "P3 Normale",
  p4: "P4 Faible",
};


function MetricCard({
  label,
  value,
  icon: Icon,
}) {
  return (
    <div
      className="
        rounded-[18px]
        p-5
      "
      style={{
        background:
          "var(--do-card-bg)",
        border:
          "1px solid var(--do-border)",
      }}
    >
      <div
        className="
          flex items-start
          justify-between
          gap-3
        "
      >
        <div>
          <div
            className="
              label-eyebrow
              mb-2
            "
          >
            {label}
          </div>

          <div
            className="
              font-display
              text-2xl
            "
            style={{
              color:
                "var(--do-ink)",
            }}
          >
            {value ?? 0}
          </div>
        </div>

        <Icon
          size={20}
          weight="bold"
          style={{
            color:
              "var(--do-accent)",
          }}
        />
      </div>
    </div>
  );
}


export default function PlatformSupport({ initialTicketId = "" }) {
  const [
    data,
    setData,
  ] = useState(
    null
  );

  const [
    selectedTicket,
    setSelectedTicket,
  ] = useState(
    null
  );

  const [
    events,
    setEvents,
  ] = useState(
    []
  );

  const [
    internalNote,
    setInternalNote,
  ] = useState(
    ""
  );

  const [
    publicMessage,
    setPublicMessage,
  ] = useState(
    ""
  );

  const [
    saving,
    setSaving,
  ] = useState(
    false
  );

  const [
    previewAttachment,
    setPreviewAttachment,
  ] = useState(
    null
  );

  const [
    loading,
    setLoading,
  ] = useState(
    true
  );

  const [
    filters,
    setFilters,
  ] = useState({
    page: 1,
    limit: 20,
    search: "",
    status: "",
    priority: "",
    type: "",
  });

  const loadTickets = useCallback(
    async () => {
      setLoading(
        true
      );

      try {
        const result =
          await platformSupport
            .listTickets(
              filters
            );

        setData(
          result
        );
      } catch (error) {
        toast.error(
          error
            ?.response
            ?.data
            ?.detail
          || "Impossible de charger le support."
        );
      } finally {
        setLoading(
          false
        );
      }
    },
    [filters]
  );

  useEffect(
    () => {
      const timer =
        window.setTimeout(
          loadTickets,
          filters.search
            ? 300
            : 0
        );

      return () =>
        window.clearTimeout(
          timer
        );
    },
    [
      loadTickets,
      filters.search,
    ]
  );

  const updateFilter = (
    name,
    value
  ) => {
    setFilters(
      (current) => ({
        ...current,
        [name]: value,
        page:
          name === "page"
            ? value
            : 1,
      })
    );
  };

  const openTicket = async (
    ticketId
  ) => {
    try {
      const result =
        await platformSupport
          .getTicket(
            ticketId
          );

      setSelectedTicket(
        result.ticket
      );

      setEvents(
        result.events || []
      );

      setInternalNote(
        ""
      );

      setPublicMessage(
        ""
      );
    } catch (error) {
      toast.error(
        error
          ?.response
          ?.data
          ?.detail
        || "Ticket introuvable."
      );
    }
  };

  useEffect(() => {
    if (initialTicketId) {
      openTicket(
        initialTicketId
      );
    }
  }, [initialTicketId]);

  const updateTicket = async (
    payload
  ) => {
    if (!selectedTicket) {
      return;
    }

    setSaving(
      true
    );

    try {
      const result =
        await platformSupport
          .updateTicket(
            selectedTicket.id,
            payload
          );

      setSelectedTicket(
        result.ticket
      );

      setEvents(
        result.events || []
      );

      setInternalNote(
        ""
      );

      setPublicMessage(
        ""
      );

      await loadTickets();

      toast.success(
        "Ticket mis à jour"
      );
    } catch (error) {
      toast.error(
        error
          ?.response
          ?.data
          ?.detail
        || "Mise à jour impossible."
      );
    } finally {
      setSaving(
        false
      );
    }
  };

  const sendPublicMessage = async () => {
    if (!selectedTicket || !publicMessage.trim()) {
      return;
    }

    setSaving(true);

    try {
      await platformSupport.addMessage(
        selectedTicket.id,
        publicMessage.trim()
      );

      setPublicMessage("");

      const result =
        await platformSupport.getTicket(
          selectedTicket.id
        );

      setSelectedTicket(
        result.ticket
      );

      setEvents(
        result.events || []
      );

      await loadTickets();

      toast.success(
        "Réponse envoyée au demandeur"
      );
    } catch (error) {
      toast.error(
        error?.response?.data?.detail
        || "Réponse impossible."
      );
    } finally {
      setSaving(false);
    }
  };


  const summary =
    data?.summary || {};

  const inputStyle = {
    background:
      "var(--do-bg-solid)",
    borderColor:
      "var(--do-border)",
    color:
      "var(--do-ink)",
  };

  return (
    <section
      className="space-y-6"
      data-testid="platform-support"
    >
      <div>
        <div
          className="
            label-eyebrow
            mb-1
          "
        >
          Centre de support
        </div>

        <h2
          className="
            font-heading
            text-2xl
            font-bold
          "
          style={{
            color:
              "var(--do-ink)",
          }}
        >
          Signalements de toute la plateforme
        </h2>

        <p
          className="mt-1 text-sm"
          style={{
            color:
              "var(--do-muted)",
          }}
        >
          Qualifiez, priorisez et suivez les
          demandes remontées par les
          utilisateurs AlmaCRM.
        </p>
      </div>

      <div
        className="
          grid gap-4
          sm:grid-cols-2
          xl:grid-cols-5
        "
      >
        <MetricCard
          label="Nouveaux"
          value={summary.new}
          icon={Bug}
        />

        <MetricCard
          label="Critiques"
          value={summary.critical}
          icon={WarningCircle}
        />

        <MetricCard
          label="En cours"
          value={summary.inProgress}
          icon={Clock}
        />

        <MetricCard
          label="En attente"
          value={summary.waiting}
          icon={Clock}
        />

        <MetricCard
          label="Résolus"
          value={summary.resolved}
          icon={CheckCircle}
        />
      </div>

      <div
        className="
          rounded-[18px]
          p-4
        "
        style={{
          background:
            "var(--do-card-bg)",
          border:
            "1px solid var(--do-border)",
        }}
      >
        <div
          className="
            grid gap-3
            md:grid-cols-2
            xl:grid-cols-5
          "
        >
          <input
            value={filters.search}
            onChange={
              (event) =>
                updateFilter(
                  "search",
                  event.target.value
                )
            }
            placeholder="Référence, titre, tenant, email…"
            className="
              rounded-[10px]
              border
              px-3 py-2
              text-sm
            "
            style={inputStyle}
          />

          <select
            value={filters.status}
            onChange={
              (event) =>
                updateFilter(
                  "status",
                  event.target.value
                )
            }
            className="
              rounded-[10px]
              border
              px-3 py-2
              text-sm
            "
            style={inputStyle}
          >
            <option value="">
              Tous les statuts
            </option>

            {Object.entries(
              STATUS_LABELS
            ).map(
              ([value, label]) => (
                <option
                  key={value}
                  value={value}
                >
                  {label}
                </option>
              )
            )}
          </select>

          <select
            value={
              filters.priority
            }
            onChange={
              (event) =>
                updateFilter(
                  "priority",
                  event.target.value
                )
            }
            className="
              rounded-[10px]
              border
              px-3 py-2
              text-sm
            "
            style={inputStyle}
          >
            <option value="">
              Toutes les priorités
            </option>

            {Object.entries(
              PRIORITY_LABELS
            ).map(
              ([value, label]) => (
                <option
                  key={value}
                  value={value}
                >
                  {label}
                </option>
              )
            )}
          </select>

          <select
            value={filters.type}
            onChange={
              (event) =>
                updateFilter(
                  "type",
                  event.target.value
                )
            }
            className="
              rounded-[10px]
              border
              px-3 py-2
              text-sm
            "
            style={inputStyle}
          >
            <option value="">
              Tous les types
            </option>

            <option value="bug">
              Bug
            </option>

            <option value="improvement">
              Amélioration
            </option>

            <option value="idea">
              Idée
            </option>

            <option value="question">
              Question
            </option>

            <option value="remark">
              Remarque
            </option>
          </select>

          <button
            type="button"
            onClick={
              () =>
                setFilters({
                  page: 1,
                  limit: 20,
                  search: "",
                  status: "",
                  priority: "",
                  type: "",
                })
            }
            className="
              rounded-[10px]
              px-3 py-2
              text-sm
              font-semibold
            "
            style={{
              border:
                "1px solid var(--do-border)",
              color:
                "var(--do-ink)",
            }}
          >
            Réinitialiser
          </button>
        </div>
      </div>

      <div
        className="
          overflow-hidden
          rounded-[18px]
        "
        style={{
          background:
            "var(--do-card-bg)",
          border:
            "1px solid var(--do-border)",
        }}
      >
        <div
          className="overflow-x-auto"
        >
          <table
            className="
              w-full
              min-w-[980px]
              text-sm
            "
          >
            <thead>
              <tr
                style={{
                  borderBottom:
                    "1px solid var(--do-border)",
                  color:
                    "var(--do-muted)",
                }}
              >
                <th className="p-4 text-left">
                  Référence
                </th>

                <th className="p-4 text-left">
                  Priorité
                </th>

                <th className="p-4 text-left">
                  Statut
                </th>

                <th className="p-4 text-left">
                  Titre
                </th>

                <th className="p-4 text-left">
                  Tenant
                </th>

                <th className="p-4 text-left">
                  Demandeur
                </th>

                <th className="p-4 text-left">
                  Dernière activité
                </th>
              </tr>
            </thead>

            <tbody>
              {(data?.items || []).map(
                (ticket) => (
                  <tr
                    key={ticket.id}
                    onClick={
                      () =>
                        openTicket(
                          ticket.id
                        )
                    }
                    className="
                      cursor-pointer
                      transition-colors
                    "
                    style={{
                      borderBottom:
                        "1px solid var(--do-border)",
                    }}
                  >
                    <td
                      className="
                        p-4
                        font-semibold
                      "
                    >
                      {ticket.reference}
                    </td>

                    <td className="p-4">
                      {PRIORITY_LABELS[
                        ticket.priority
                      ] || ticket.priority}
                    </td>

                    <td className="p-4">
                      {STATUS_LABELS[
                        ticket.status
                      ] || ticket.status}
                    </td>

                    <td
                      className="
                        max-w-[320px]
                        truncate
                        p-4
                      "
                    >
                      {ticket.title}
                    </td>

                    <td className="p-4">
                      {ticket.tenantName
                        || ticket.tenantId
                        || "—"}
                    </td>

                    <td className="p-4">
                      {ticket.reporterEmail
                        || "—"}
                    </td>

                    <td className="p-4">
                      {ticket.updatedAt
                        ? new Date(
                            ticket.updatedAt
                          ).toLocaleString(
                            "fr-FR"
                          )
                        : "—"}
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>

        {loading && (
          <div
            className="
              p-8
              text-center
              text-sm
            "
            style={{
              color:
                "var(--do-muted)",
            }}
          >
            Chargement des tickets…
          </div>
        )}

        {!loading
          && !(data?.items || []).length
          && (
            <div
              className="
                p-8
                text-center
                text-sm
              "
              style={{
                color:
                  "var(--do-muted)",
              }}
            >
              Aucun ticket ne correspond aux filtres.
            </div>
          )}

        <div
          className="
            flex items-center
            justify-between
            gap-3
            p-4
          "
        >
          <span
            className="text-xs"
            style={{
              color:
                "var(--do-muted)",
            }}
          >
            Page {data?.page || 1}/
            {data?.totalPages || 1}
            {" · "}
            {data?.total || 0}
            {" ticket(s)"}
          </span>

          <div
            className="flex gap-2"
          >
            <button
              type="button"
              disabled={
                (data?.page || 1)
                <= 1
              }
              onClick={
                () =>
                  updateFilter(
                    "page",
                    Math.max(
                      1,
                      filters.page - 1
                    )
                  )
              }
              className="
                rounded-[9px]
                px-3 py-2
                text-xs
                font-semibold
                disabled:opacity-40
              "
              style={{
                border:
                  "1px solid var(--do-border)",
              }}
            >
              Précédent
            </button>

            <button
              type="button"
              disabled={
                (data?.page || 1)
                >= (
                  data?.totalPages
                  || 1
                )
              }
              onClick={
                () =>
                  updateFilter(
                    "page",
                    filters.page + 1
                  )
              }
              className="
                rounded-[9px]
                px-3 py-2
                text-xs
                font-semibold
                disabled:opacity-40
              "
              style={{
                border:
                  "1px solid var(--do-border)",
              }}
            >
              Suivant
            </button>
          </div>
        </div>
      </div>

      {selectedTicket && (
        <div
          className="
            fixed inset-0
            z-[100]
            flex justify-end
          "
        >
          <button
            type="button"
            className="
              absolute inset-0
              bg-[var(--do-overlay)]
            "
            aria-label="Fermer"
            onClick={
              () =>
                setSelectedTicket(
                  null
                )
            }
          />

          <aside
            className="
              relative z-10
              h-full
              w-full
              max-w-2xl
              overflow-y-auto
              p-5 sm:p-6
            "
            style={{
              background:
                "var(--do-bg-solid)",
              borderLeft:
                "1px solid var(--do-border)",
            }}
          >
            <div
              className="
                flex items-start
                justify-between
                gap-4
              "
            >
              <div>
                <div
                  className="label-eyebrow"
                >
                  {selectedTicket.reference}
                </div>

                <h3
                  className="
                    mt-1
                    font-heading
                    text-xl
                    font-bold
                  "
                >
                  {selectedTicket.title}
                </h3>

                <p
                  className="
                    mt-1
                    text-xs
                  "
                  style={{
                    color:
                      "var(--do-muted)",
                  }}
                >
                  {selectedTicket.tenantName
                    || selectedTicket.tenantId
                    || "Tenant inconnu"}
                  {" · "}
                  {selectedTicket.reporterEmail
                    || "Email inconnu"}
                </p>
              </div>

              <button
                type="button"
                onClick={
                  () =>
                    setSelectedTicket(
                      null
                    )
                }
                className="
                  text-sm
                  font-semibold
                "
              >
                Fermer
              </button>
            </div>

            <div
              className="
                mt-5
                grid gap-3
                sm:grid-cols-2
              "
            >
              <label
                className="space-y-1"
              >
                <span
                  className="
                    text-xs
                    font-semibold
                  "
                >
                  Statut
                </span>

                <select
                  value={
                    selectedTicket.status
                  }
                  disabled={saving}
                  onChange={
                    (event) =>
                      updateTicket({
                        status:
                          event.target.value,
                      })
                  }
                  className="
                    w-full
                    rounded-[10px]
                    border
                    px-3 py-2
                    text-sm
                  "
                  style={inputStyle}
                >
                  {Object.entries(
                    STATUS_LABELS
                  ).map(
                    ([value, label]) => (
                      <option
                        key={value}
                        value={value}
                      >
                        {label}
                      </option>
                    )
                  )}
                </select>
              </label>

              <label
                className="space-y-1"
              >
                <span
                  className="
                    text-xs
                    font-semibold
                  "
                >
                  Priorité
                </span>

                <select
                  value={
                    selectedTicket.priority
                  }
                  disabled={saving}
                  onChange={
                    (event) =>
                      updateTicket({
                        priority:
                          event.target.value,
                      })
                  }
                  className="
                    w-full
                    rounded-[10px]
                    border
                    px-3 py-2
                    text-sm
                  "
                  style={inputStyle}
                >
                  {Object.entries(
                    PRIORITY_LABELS
                  ).map(
                    ([value, label]) => (
                      <option
                        key={value}
                        value={value}
                      >
                        {label}
                      </option>
                    )
                  )}
                </select>
              </label>
            </div>

            <div
              className="
                mt-5
                rounded-[16px]
                p-4
              "
              style={{
                background:
                  "var(--do-card-bg)",
                border:
                  "1px solid var(--do-border)",
              }}
            >
              <div
                className="
                  label-eyebrow
                  mb-2
                "
              >
                Description
              </div>

              <p
                className="
                  whitespace-pre-wrap
                  text-sm
                "
              >
                {selectedTicket.description}
              </p>
            </div>

            {(selectedTicket.attachments || []).map(
              (attachment) => (
                <button
                  type="button"
                  key={
                    attachment.downloadUrl
                  }
                  onClick={
                    () =>
                      setPreviewAttachment(
                        attachment
                      )
                  }
                  className="
                    mt-4
                    block
                    w-full
                    overflow-hidden
                    rounded-[14px]
                    text-left
                  "
                  style={{
                    border:
                      "1px solid var(--do-border)",
                    background:
                      "var(--do-card-bg)",
                  }}
                >
                  <img
                    src={
                      attachment.downloadUrl
                    }
                    alt={
                      `Capture ${
                        attachment.name
                        || selectedTicket.reference
                      }`
                    }
                    className="
                      max-h-72
                      w-full
                      bg-[var(--do-overlay)]
                      object-contain
                    "
                  />

                  <span
                    className="
                      flex
                      items-center
                      gap-2
                      px-4 py-3
                      text-sm
                      font-semibold
                    "
                    style={{
                      color:
                        "var(--do-accent)",
                    }}
                  >
                    <ImageSquare
                      size={18}
                      weight="bold"
                    />

                    Voir la capture — {attachment.name}
                  </span>
                </button>
              )
            )}

            <div
              className="
                mt-5
                rounded-[16px]
                p-4
              "
              style={{
                background:
                  "var(--do-card-bg)",
                border:
                  "1px solid var(--do-border)",
              }}
            >
              <div
                className="
                  label-eyebrow
                  mb-2
                "
              >
                Contexte technique
              </div>

              <div
                className="space-y-2"
              >
                {Object.entries(
                  selectedTicket
                    .technicalContext
                  || {}
                ).map(
                  ([key, value]) => (
                    <div
                      key={key}
                      className="
                        grid
                        grid-cols-[140px_1fr]
                        gap-2
                        text-xs
                      "
                    >
                      <span
                        style={{
                          color:
                            "var(--do-muted)",
                        }}
                      >
                        {key}
                      </span>

                      <span
                        className="break-all"
                      >
                        {String(
                          value || "—"
                        )}
                      </span>
                    </div>
                  )
                )}
              </div>
            </div>

            <div
              className="mt-5"
            >
              <div
                className="
                  label-eyebrow
                  mb-2
                "
              >
                Réponse au demandeur
              </div>

              <textarea
                value={publicMessage}
                onChange={
                  (event) =>
                    setPublicMessage(
                      event.target.value
                    )
                }
                rows={4}
                maxLength={5000}
                placeholder="Message visible par l'utilisateur…"
                className="
                  w-full
                  rounded-[11px]
                  border
                  px-3 py-2
                  text-sm
                "
                style={inputStyle}
                data-testid="support-admin-public-message"
              />

              <button
                type="button"
                disabled={
                  saving
                  || !publicMessage.trim()
                }
                onClick={
                  sendPublicMessage
                }
                className="
                  mt-2
                  rounded-[10px]
                  px-4 py-2
                  text-sm
                  font-semibold
                  disabled:opacity-40
                "
                style={{
                  background:
                    "linear-gradient(135deg, var(--do-blue), var(--do-navy))",
                  color: "#FFFFFF",
                }}
                data-testid="support-admin-send-public-message"
              >
                Envoyer au demandeur
              </button>
            </div>

            <div
              className="mt-5"
            >
              <div
                className="
                  label-eyebrow
                  mb-2
                "
              >
                Note interne
              </div>

              <textarea
                value={internalNote}
                onChange={
                  (event) =>
                    setInternalNote(
                      event.target.value
                    )
                }
                rows={4}
                placeholder="Analyse, action réalisée, référence du commit…"
                className="
                  w-full
                  rounded-[11px]
                  border
                  px-3 py-2
                  text-sm
                "
                style={inputStyle}
              />

              <button
                type="button"
                disabled={
                  saving
                  || !internalNote.trim()
                }
                onClick={
                  () =>
                    updateTicket({
                      internalNote:
                        internalNote.trim(),
                    })
                }
                className="
                  mt-2
                  rounded-[10px]
                  px-4 py-2
                  text-sm
                  font-semibold
                  disabled:opacity-40
                "
                style={{
                  background:
                    "linear-gradient(135deg, var(--do-blue), var(--do-navy))",
                  color: "#FFFFFF",
                }}
              >
                Ajouter la note
              </button>
            </div>

            <div
              className="mt-6"
            >
              <div
                className="
                  label-eyebrow
                  mb-3
                "
              >
                Historique
              </div>

              <div
                className="space-y-3"
              >
                {events.map(
                  (event) => (
                    <div
                      key={event.id}
                      className="
                        rounded-[12px]
                        p-3
                        text-xs
                      "
                      style={{
                        border:
                          "1px solid var(--do-border)",
                      }}
                    >
                      <div
                        className="font-semibold"
                      >
                        {event.type}
                        {" · "}
                        {event.actorEmail
                          || "système"}
                      </div>

                      <div
                        className="mt-1"
                        style={{
                          color:
                            "var(--do-muted)",
                        }}
                      >
                        {event.createdAt
                          ? new Date(
                              event.createdAt
                            ).toLocaleString(
                              "fr-FR"
                            )
                          : "—"}
                      </div>

                      {event
                        .payload
                        ?.internalNote
                        && (
                          <p
                            className="
                              mt-2
                              whitespace-pre-wrap
                            "
                          >
                            {
                              event
                                .payload
                                .internalNote
                            }
                          </p>
                        )}

                      {event
                        .payload
                        ?.message
                        && (
                          <p
                            className="
                              mt-2
                              whitespace-pre-wrap
                            "
                          >
                            {
                              event
                                .payload
                                .message
                            }
                          </p>
                        )}
                    </div>
                  )
                )}
              </div>
            </div>
          </aside>
        </div>
      )}

      {previewAttachment && (
        <div
          className="
            fixed inset-0
            z-[170]
            flex flex-col
            bg-[var(--do-overlay)]
          "
          role="dialog"
          aria-modal="true"
          data-acta-modal-kind="viewer"
          aria-label="Aperçu de la capture"
        >
          <div
            className="
              flex
              items-center
              justify-between
              gap-3
              border-b
              border-[var(--do-border)]
              px-4 py-3
            "
          >
            <div
              className="
                truncate
                text-sm
                font-semibold
                text-[var(--do-on-accent)]
              "
            >
              {
                previewAttachment.name
                || "Capture"
              }
            </div>

            <div
              className="
                flex
                items-center
                gap-2
              "
            >
              <a
                href={
                  previewAttachment
                    .downloadUrl
                }
                target="_blank"
                rel="noreferrer"
                className="
                  rounded-[9px]
                  border
                  border-[var(--do-border)]
                  px-3 py-2
                  text-xs
                  font-semibold
                  text-[var(--do-on-accent)]
                "
              >
                Ouvrir l’original
              </a>

              <a
                href={
                  previewAttachment
                    .downloadUrl
                }
                download
                className="
                  rounded-[9px]
                  border
                  border-[var(--do-border)]
                  px-3 py-2
                  text-xs
                  font-semibold
                  text-[var(--do-on-accent)]
                "
              >
                Télécharger
              </a>

              <button
                type="button"
                onClick={
                  () =>
                    setPreviewAttachment(
                      null
                    )
                }
                className="
                  inline-flex
                  h-9 w-9
                  items-center
                  justify-center
                  rounded-[9px]
                  bg-[var(--do-bg-solid)]
                  text-[var(--do-ink)]
                "
                aria-label="Fermer l’aperçu"
              >
                <X
                  size={18}
                  weight="bold"
                />
              </button>
            </div>
          </div>

          <div
            className="
              flex flex-1
              items-center
              justify-center
              overflow-auto
              p-4 sm:p-8
            "
          >
            <img
              src={
                previewAttachment
                  .downloadUrl
              }
              alt={
                previewAttachment.name
                || "Capture"
              }
              className="
                max-h-full
                max-w-full
                rounded-[12px]
                bg-[var(--do-bg-solid)]
                object-contain
                shadow-2xl
              "
            />
          </div>
        </div>
      )}
    </section>
  );
}
